import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Wordmark } from "./Wordmark";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { useLang } from "../../i18n/LanguageContext";

const navItems = [
  { key: "home", href: "#home" },
  { key: "about", href: "#about" },
  { key: "whatWeDo", href: "#what-we-do" },
  { key: "investmentApproach", href: "#investment-approach" },
  { key: "contact", href: "#contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    if (pathname !== "/") {
      navigate("/" + href);
      return;
    }
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#F4F3EF]/85 backdrop-blur-xl border-b border-stone-300/40 py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => go("#home")}
          aria-label="M&K Properties home"
        >
          <Wordmark light={!scrolled} />
        </button>

        <nav className="hidden lg:flex items-center gap-10">
          {navItems.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.href.replace("#", "")}`}
              onClick={() => go(l.href)}
              className={`text-sm font-medium transition-colors duration-300 relative after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 hover:after:w-full after:transition-all after:duration-300 ${
                scrolled
                  ? "text-[#1A1C19] hover:text-[#5C5C59] after:bg-[#1A1C19]"
                  : "text-[#F4F3EF]/90 hover:text-white after:bg-[#F4F3EF]"
              }`}
            >
              {t.nav[l.key]}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-4 lg:gap-6">
          <LanguageSwitcher light={!scrolled} />

          <div className="hidden lg:block">
            <button
              data-testid="nav-contact-button"
              onClick={() => go("#contact")}
              className={`px-6 py-3 text-sm font-medium tracking-wide transition-colors duration-300 ${
                scrolled
                  ? "bg-[#1A1C19] text-[#F4F3EF] hover:bg-[#3A4036]"
                  : "bg-[#F4F3EF] text-[#1A1C19] hover:bg-[#B5A68B]"
              }`}
            >
              {t.nav.contactUs}
            </button>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className={`lg:hidden ${scrolled ? "text-[#1A1C19]" : "text-[#F4F3EF]"}`}
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-[#F4F3EF] border-t border-stone-300/40 mt-3"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {navItems.map((l) => (
              <button
                key={l.href}
                data-testid={`mobile-nav-link-${l.href.replace("#", "")}`}
                onClick={() => go(l.href)}
                className="text-left text-lg font-medium text-[#1A1C19]"
              >
                {t.nav[l.key]}
              </button>
            ))}
            <button
              data-testid="mobile-nav-contact-button"
              onClick={() => go("#contact")}
              className="mt-2 bg-[#1A1C19] text-[#F4F3EF] px-6 py-4 text-sm font-medium tracking-wide"
            >
              {t.nav.contactUs}
            </button>
          </div>
        </div>
      )}
    </header>
  );
};
